import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import certificatesData from "../data/certificatesData";
import CertificateCard from "./CertificateCard";

const CertificateCarousel = ({ onSelectCertificate, isViewerOpen }) => {
  const N = certificatesData.length; // Exactly 7 certificates

  // 4 sequences: prefix, middle (active), suffix, and tail
  // Guarantees viewports from mobile up to 4K are fully covered with zero empty space.
  const allCards = useMemo(() => {
    return [
      ...certificatesData.map((c) => ({ ...c, uniqueKey: `prefix-${c.id}` })),
      ...certificatesData.map((c) => ({ ...c, uniqueKey: `active-${c.id}` })),
      ...certificatesData.map((c) => ({ ...c, uniqueKey: `suffix-${c.id}` })),
      ...certificatesData.map((c) => ({ ...c, uniqueKey: `tail-${c.id}` })),
    ];
  }, []);

  const BASE_INDEX = N; // Starts at index 7 (beginning of the active set)

  // Step ranges from 0 to N (0 = Certificate 1 of active set, 7 = Certificate 1 of suffix set)
  const [step, setStep] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const [autoPlayTrigger, setAutoPlayTrigger] = useState(0);

  // Dimensions
  const [cardWidth, setCardWidth] = useState(320);
  const [containerWidth, setContainerWidth] = useState(1200);
  const GAP = 24; // Tailwind gap-6 is 24px
  const slotWidth = cardWidth + GAP;

  const containerRef = useRef(null);
  const firstCardRef = useRef(null);
  const touchStartRef = useRef({ x: 0, y: 0 });

  // Detect user prefers-reduced-motion
  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    setPrefersReducedMotion(mediaQuery.matches);
    const handler = (e) => setPrefersReducedMotion(e.matches);
    mediaQuery.addEventListener?.("change", handler);
    return () => mediaQuery.removeEventListener?.("change", handler);
  }, []);

  // Responsive Measurement via ResizeObserver
  const updateDimensions = useCallback(() => {
    if (containerRef.current) {
      setContainerWidth(containerRef.current.offsetWidth);
    }
    if (firstCardRef.current) {
      const w = firstCardRef.current.offsetWidth;
      if (w > 0) setCardWidth(w);
    }
  }, []);

  useEffect(() => {
    updateDimensions();
    window.addEventListener("resize", updateDimensions);

    let resizeObserver;
    if (window.ResizeObserver && containerRef.current) {
      resizeObserver = new ResizeObserver(() => updateDimensions());
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      window.removeEventListener("resize", updateDimensions);
      if (resizeObserver) resizeObserver.disconnect();
    };
  }, [updateDimensions]);

  // Restart 5-second auto-play timer upon manual navigation
  const restartTimer = useCallback(() => {
    setAutoPlayTrigger((prev) => prev + 1);
  }, []);

  // Advance to next certificate
  const handleNext = useCallback(() => {
    setIsTransitioning(true);
    setStep((prev) => (prev >= N ? 1 : prev + 1));
    restartTimer();
  }, [N, restartTimer]);

  // Go to previous certificate
  const handlePrev = useCallback(() => {
    setStep((prev) => {
      if (prev > 0) {
        setIsTransitioning(true);
        return prev - 1;
      } else {
        // Silently jump to step N (identical Certificate 1) without transition
        setIsTransitioning(false);
        // Animate from step N to N - 1 (Certificate 7) in the next frame
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setIsTransitioning(true);
            setStep(N - 1);
          });
        });
        return N;
      }
    });
    restartTimer();
  }, [N, restartTimer]);

  // Silent reset when step reaches N (after Certificate 1 of suffix set slides in)
  useEffect(() => {
    if (step >= N) {
      // Allow the 650ms transition to complete, then silently reset to step 0
      const timer = setTimeout(() => {
        setIsTransitioning(false);
        setStep(0);
      }, 660);

      return () => clearTimeout(timer);
    }
  }, [step, N]);

  // Automatic slide: advances every 5 seconds (5000 milliseconds)
  // Starts on mount, pauses ONLY when full-size viewer is open, and resumes from previous position.
  useEffect(() => {
    if (isViewerOpen) return;

    const interval = setInterval(() => {
      setIsTransitioning(true);
      setStep((prev) => (prev >= N ? 1 : prev + 1));
    }, 5000);

    return () => clearInterval(interval);
  }, [isViewerOpen, N, autoPlayTrigger]);

  // Touch Swipe handlers for mobile
  const handleTouchStart = (e) => {
    touchStartRef.current = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY,
    };
  };

  const handleTouchEnd = (e) => {
    const deltaX = e.changedTouches[0].clientX - touchStartRef.current.x;
    const deltaY = e.changedTouches[0].clientY - touchStartRef.current.y;
    if (Math.abs(deltaX) > 40 && Math.abs(deltaX) > Math.abs(deltaY)) {
      if (deltaX < 0) {
        handleNext();
      } else {
        handlePrev();
      }
    }
  };

  // Keyboard navigation
  const handleContainerKeyDown = (e) => {
    if (e.key === "ArrowLeft") {
      e.preventDefault();
      handlePrev();
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      handleNext();
    }
  };

  // Physical card index currently centered
  const physicalIndex = BASE_INDEX + step;

  // Center offset ensures the active card sits in the center of the viewport
  const centerOffset = (containerWidth - cardWidth) / 2;
  const translateX = centerOffset - physicalIndex * slotWidth;

  // Active dot index (0..6)
  const activeDotIndex = step % N;

  return (
    <div
      className="relative w-full overflow-hidden py-4 select-none"
      onKeyDown={handleContainerKeyDown}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      aria-roledescription="carousel"
      aria-label="Affiliation and Accreditation Certificates Carousel"
    >
      {/* Ambient Lateral Vignettes / Fading Gradients */}
      <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-12 sm:w-28 md:w-36 bg-gradient-to-r from-[#060B1F] via-[#060B1F]/80 to-transparent z-20" />
      <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-12 sm:w-28 md:w-36 bg-gradient-to-l from-[#060B1F] via-[#060B1F]/80 to-transparent z-20" />

      {/* Manual Left Arrow */}
      <button
        onClick={handlePrev}
        aria-label="Previous certificate"
        className="absolute left-3 sm:left-8 top-1/2 -translate-y-1/2 z-30 w-11 h-11 rounded-full bg-slate-900/85 hover:bg-[#7C4DFF] border border-white/15 hover:border-[#7C4DFF] text-white flex items-center justify-center shadow-[0_4px_20px_rgba(0,0,0,0.5)] transition-all duration-300 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-[#27B8FF]"
      >
        <ChevronLeft className="w-5 h-5" />
      </button>

      {/* Manual Right Arrow */}
      <button
        onClick={handleNext}
        aria-label="Next certificate"
        className="absolute right-3 sm:right-8 top-1/2 -translate-y-1/2 z-30 w-11 h-11 rounded-full bg-slate-900/85 hover:bg-[#7C4DFF] border border-white/15 hover:border-[#7C4DFF] text-white flex items-center justify-center shadow-[0_4px_20px_rgba(0,0,0,0.5)] transition-all duration-300 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-[#27B8FF]"
      >
        <ChevronRight className="w-5 h-5" />
      </button>

      {/* Carousel Track Viewport */}
      <div ref={containerRef} className="w-full overflow-hidden px-4">
        <div
          className="flex items-center gap-6 will-change-transform py-6"
          style={{
            transform: `translate3d(${translateX}px, 0, 0)`,
            transition: isTransitioning
              ? prefersReducedMotion
                ? "none"
                : "transform 650ms cubic-bezier(0.25, 1, 0.5, 1)"
              : "none",
          }}
        >
          {allCards.map((item, idx) => {
            const isCenter = idx === physicalIndex;
            return (
              <CertificateCard
                key={item.uniqueKey}
                certificate={item}
                isActive={isCenter}
                cardRef={idx === 0 ? firstCardRef : null}
                onClick={() => onSelectCertificate(item)}
              />
            );
          })}
        </div>
      </div>

      {/* Indicator Dots */}
      <div className="flex items-center justify-center gap-2 mt-6 z-20 relative">
        {certificatesData.map((cert, index) => {
          const isSelected = index === activeDotIndex;
          return (
            <button
              key={cert.id}
              onClick={() => {
                setIsTransitioning(true);
                setStep(index);
                restartTimer();
              }}
              aria-label={`Go to certificate ${index + 1}: ${cert.title}`}
              className={`h-2 rounded-full transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-[#27B8FF] ${
                isSelected
                  ? "w-8 bg-gradient-to-r from-[#7C4DFF] to-[#27B8FF] shadow-[0_0_12px_rgba(124,77,255,0.6)]"
                  : "w-2 bg-white/20 hover:bg-white/40"
              }`}
            />
          );
        })}
      </div>
    </div>
  );
};

export default React.memo(CertificateCarousel);
